import { useEffect, useState } from "react";

import { getWikipediaImages } from "../utils/wikiImages";

/**
 * Wikipedia thumbnails for the city browse grid, keyed by city name.
 *
 * Every card in the grid mounts at once, so fetching per card would mean ~120
 * requests. `getWikipediaImages` batches 50 titles per call and caches across
 * mounts, so a full grid costs two or three requests the first time and none
 * after that.
 *
 * A city missing from the returned map is still loading; a city mapped to
 * `null` has no usable image and should fall back to the placeholder.
 */
export function useCityPhotos(cityNames: string[], thumbnailSize = 240) {
  const [photos, setPhotos] = useState<Record<string, string | null>>({});

  // The joined names *are* the identity of the request — a new array with the
  // same contents must not refire it.
  const namesKey = cityNames.join("|");

  useEffect(() => {
    if (!namesKey) {
      setPhotos({});
      return;
    }

    let cancelled = false;
    getWikipediaImages(namesKey.split("|"), thumbnailSize).then((resolved) => {
      if (!cancelled) setPhotos(resolved);
    });

    return () => {
      cancelled = true;
    };
  }, [namesKey, thumbnailSize]);

  return photos;
}
